import React from 'react';
import ModuleRenderer from './ModuleRenderer';
import EnvelopeExperience from './opening/EnvelopeExperience';
import FullPageExperience from './opening/FullPageExperience';
import LayeredGiftExperience from './opening/LayeredGiftExperience';
import MemoryJourneyExperience from './opening/MemoryJourneyExperience';
import QuietExperience from './opening/QuietExperience';
import SurpriseLockExperience from './opening/SurpriseLockExperience';

export const OPENING_EXPERIENCE_META = {
  layered_gift: {
    label: 'Layer-by-layer Gift',
    rhythm: 'One chapter at a time',
    intent: 'A guided reveal where recipients open each gift chapter before moving to the next.',
  },
  gift_unboxing: {
    label: 'Gift Unboxing',
    rhythm: 'Layered reveal',
    intent: 'A wrapped-gift flow where each chapter feels like another layer being opened.',
  },
  envelope: {
    label: 'Envelope',
    rhythm: 'Letter by letter',
    intent: 'A private paper-and-envelope experience for letters, gratitude, and romance.',
  },
  photo_album: {
    label: 'Photo Album',
    rhythm: 'Page turn',
    intent: 'A photo-first reading mode where captions support the images instead of competing with them.',
  },
  memory_journey: {
    label: 'Memory Journey',
    rhythm: 'Path of moments',
    intent: 'A guided route through stops, milestones, and the emotional arc of the gift.',
  },
  surprise_lock: {
    label: 'Surprise Lock',
    rhythm: 'Unlock sequence',
    intent: 'A playful reveal that uses tap, hold, swipe, or prompts to unlock the next moment.',
  },
  quiet: {
    label: 'Quiet Keepsake',
    rhythm: 'Soft reading',
    intent: 'A calm, respectful reading mode for family, memorial, and gentler gifts.',
  },
  classic: {
    label: 'Classic Keepsake',
    rhythm: 'Complete page',
    intent: 'A full-page story for recipients who should see everything in one smooth scroll.',
  },
  poster: {
    label: 'Keepsake Poster',
    rhythm: 'Final card',
    intent: 'A compact, saveable ending that frames the whole gift around one keepsake moment.',
  },
};

const OPENING_MODE_ALIASES = {
  gift_reveal: 'gift_unboxing',
  full_page: 'classic',
  memory_page: 'classic',
  letters_for_later: 'envelope',
  open_when: 'envelope',
  photo_journey: 'photo_album',
  keepsake_moment: 'poster',
  keepsake_poster: 'poster',
  quiet_keepsake: 'quiet',
};

export const normalizeOpeningMode = (openingMode = 'layered_gift') => {
  const key = openingMode || 'layered_gift';
  return OPENING_MODE_ALIASES[key] || key;
};

const EXPERIENCES = {
  layered_gift: LayeredGiftExperience,
  gift_unboxing: LayeredGiftExperience,
  classic: FullPageExperience,
  envelope: EnvelopeExperience,
  photo_album: MemoryJourneyExperience,
  memory_journey: MemoryJourneyExperience,
  surprise_lock: SurpriseLockExperience,
  quiet: QuietExperience,
  poster: QuietExperience,
};

const OpeningExperienceRenderer = ({
  modules,
  isEditing,
  updateModule,
  removeModule,
  pageId,
  isLocked,
  openingMode = 'layered_gift',
  mediaUsage,
  focusedModuleId,
  pageData,
  updateModuleConfig,
  theme,
  onComplete,
  startAtFirstLayer = false,
}) => {
  if (!modules || !Array.isArray(modules)) return null;

  if (isEditing && !focusedModuleId) return null;

  const visibleModules = isEditing ?
    modules.filter((module) => (module.id || module._localId) === focusedModuleId) :
    modules;

  const renderModules = (items, mode = 'full_page') => (
    <div className="w-full overflow-visible">
      <ModuleRenderer
        modules={items}
        isEditing={isEditing}
        updateModule={updateModule}
        removeModule={removeModule}
        pageId={pageId}
        isLocked={isLocked}
        openingMode={mode}
        mediaUsage={mediaUsage}
        focusedModuleId={focusedModuleId}
        updateModuleConfig={updateModuleConfig}
        theme={theme}
      />
    </div>
  );

  if (isEditing) {
    return renderModules(visibleModules, 'full_page');
  }

  const normalizedOpeningMode = normalizeOpeningMode(openingMode);
  const experienceMeta = OPENING_EXPERIENCE_META[normalizedOpeningMode] || OPENING_EXPERIENCE_META.layered_gift;
  const Experience = EXPERIENCES[normalizedOpeningMode] || LayeredGiftExperience;

  return (
    <Experience
      modules={visibleModules}
      renderModules={renderModules}
      openingMode={normalizedOpeningMode}
      experienceMeta={experienceMeta}
      onComplete={onComplete}
      startAtFirstLayer={startAtFirstLayer}
      giftIntro={{
        pageData,
        pageId,
        title: pageData?.openingTitle,
        subtitle: pageData?.openingSubtitle,
        ownerName: pageData?.ownerName,
        themeId: pageData?.themeId,
      }}
    />
  );
};

export default OpeningExperienceRenderer;
